import React, { useEffect, useState, useRef, useContext, Fragment, useCallback } from "react";
import {
  isNumeric,
  getMonthNumberToName,
  getStatusFlagValue,
  getButtonDisabledStates,
  shortMonthToMonthNumber,
  formatIipValueByYear,
  toIipStorageValueByYear,
} from "../Custom/Custom";
import messages from "../assets/Messages.json";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../Constant";
import { GlobalLoading, showLoading } from "react-global-loading";
import { validateFileForUpload } from "../utils/fileValidation";
import { faIR } from "@mui/x-date-pickers/locales";

/** No item chosen and quantity/value are empty or zero — save allowed when man & manufacturing days are both 0. */
function isIdleManufacturingRow(row) {
  if (!row) return true;
  if (row.itemCode === "000000") return false;
  const noItem = !row.itemCode || String(row.itemCode).trim() === "";
  if (!noItem) return false;
  const qRaw = row.itemQuantity;
  const pRaw = row.itemPrice;
  const q = parseFloat(qRaw);
  const p = parseFloat(pRaw);
  const qtyEmptyOrZero =
    qRaw === "" ||
    qRaw === undefined ||
    qRaw === null ||
    (!Number.isNaN(q) && q === 0);
  const priceEmptyOrZero =
    pRaw === "" ||
    pRaw === undefined ||
    pRaw === null ||
    (!Number.isNaN(p) && p === 0);
  return qtyEmptyOrZero && priceEmptyOrZero;
}

/** Open status: 0/null → empty field; user-typed 0 stays visible */
function isZeroNumeric(val) {
  if (val === undefined || val === null || val === "") return true;
  const num = parseFloat(val);
  return Number.isNaN(num) || num === 0;
}

function formatQuantityForState(val, isClose = false) {
  if (isClose) return "0.000";
  if (isZeroNumeric(val)) return "";
  return parseFloat(val).toFixed(3);
}

function formatPriceForState(val, isClose = false, year) {
  if (isClose) return "0.00";
  if (isZeroNumeric(val)) return "";
  if (year === undefined || year === null || year === "") {
    return parseFloat(val).toFixed(2);
  }
  return formatIipValueByYear(val, year);
}

function getInputDisplayValue(val) {
  if (val === undefined || val === null || val === "") return "";
  return String(val);
}

/** Previous month row usable for Repeat Previous Month (skip API placeholder empty row). */
function isRepeatablePreviousMonthItem(item) {
  if (!item) return false;
  const code = String(item.itemCode || "").trim();
  if (!code) return false;
  if (code === "000000") return true;
  const qty = parseFloat(item.itemQuantity) || 0;
  const price = parseFloat(item.itemPrice) || 0;
  return qty !== 0 || price !== 0;
}

function hasRepeatablePreviousMonthData(previousMonthData) {
  const items = previousMonthData?.[0]?.detailsOfItemsManufactured || [];
  return items.some(isRepeatablePreviousMonthItem);
}

function ManufacturingSectorData({
  allFormData,
  config,
  resetFormData,
  userInfoObject,
  yearName,
  monthName,
  url,
  type,
  status,
  districtCode,
  address,
  industryCode,
  industryList,
  payload,
  iipTitleData,
  storedLanguage,
  othersData,
  titlesData,
  previousMonthData,
  previousMonthPayload,
  previousYearMonthData,
  previousYearMonthPayload,
  onSaveSuccessRefresh,
}) {
  let allManufacturingSectorFromData = allFormData[0];
  const navigate = useNavigate();
  const [itemData, setItemData] = useState({});
  const autoSelectCloseRef = useRef({ lastKey: null, inFlight: false });
  const [totalQty, setTotalQty] = useState(0.0);
  const [grandTotalRs, setGrandTotalRs] = useState(0.0);
  const [totalMenDays, setTotalMenDays] = useState("");
  const [totalManuFacturingDays, setTotalManuFacturingDays] = useState("");
  const [totalProductionValue, setTotalProductionValue] = useState("");
  const [remarkReason, seRemarkReason] = useState("");
  const [checkedItems, setCheckedItems] = useState([]);
  const [isSaveBtnDisable, setIsSaveBtnDisable] = useState(false);
  const [isSubmitBtnDisable, setIsSubmitBtnDisable] = useState(false);
  const [isAcceptBtnDisable, setIsAcceptBtnDisable] = useState(false);
  const [isRejectBtnDisable, setIsRejectBtnDisable] = useState(false);
  const [isDeleteRow, setIsDeleteRow] = useState(false);
  const [isAddRow, setIsAddRow] = useState(false);
  const roleList = Array.isArray(userInfoObject?.roles) ? userInfoObject.roles : [];
  const isHQAdminOrHQIIPRole =
    roleList.includes("HQADM") || roleList.includes("HQIIP");
  const parsedYear = parseInt(yearName, 10);
  const parsedMonth = parseInt(shortMonthToMonthNumber(monthName), 10);
  const isApr2026OrLater =
    !Number.isNaN(parsedYear) &&
    !Number.isNaN(parsedMonth) &&
    (parsedYear > 2026 || (parsedYear === 2026 && parsedMonth >= 4));
  const allowUnitDropdownForHQBeforeApr2026 =
    isHQAdminOrHQIIPRole && !isApr2026OrLater;
  const [detailsSheetFileName, setDetailsSheetFileName] = useState("");
  const [detailsSheetFileData, setDetailsSheetFileData] = useState("");
  const [detailsSheetDownloadUrl, setDetailsSheetDownloadUrl] = useState("");
  const [detailsSheetFileObject, setDetailsSheetFileObject] = useState(null);
  const [reasonRowCheckbox, setReasonRowCheckbox] = useState({});
  const initialRowCountRef = useRef(null);
  // const {
  //   isSaveBtnDisable,
  //   isSubmitBtnDisable,
  //   isAcceptBtnDisable,
  //   isRejectBtnDisable
  // } = getButtonDisabledStates(payload, userInfoObject);

  let manufacturedData =
    allManufacturingSectorFromData.detailsOfItemsManufactured;
  let allItemsList = allManufacturingSectorFromData.allItemsList; //Item List Name
  let messageLogData = allManufacturingSectorFromData.messageLog;
  let srNo = 0;
  const initialMiscValue = allManufacturingSectorFromData.totalProductionValue;
  const isHQIIPHeadQuarter =
    userInfoObject?.userLevel === "HEAD QUARTER" &&
    Array.isArray(userInfoObject?.roles) &&
    userInfoObject.roles.includes("HQIIP");
  const isHQAdminOrHQIIPHeadQuarter =
    userInfoObject?.userLevel === "HEAD QUARTER" &&
    Array.isArray(userInfoObject?.roles) &&
    (userInfoObject.roles.includes("HQADM") ||
      userInfoObject.roles.includes("HQIIP"));

  /** CF = carried forward / file-upload flow — highlight manufactured-items block */
  const isCfSource =
    String(
      payload?.SOURCE ??
        payload?.source ??
        allManufacturingSectorFromData?.source ??
        ""
    ).toUpperCase() === "CF";

  /** Quantity variation % with green ▲ / red ▼ (Font Awesome in public index.html) */
  const renderQtyVariationPercent = (v) => {
    if (v === null || v === undefined || Number.isNaN(v)) return null;
    const showDirection = Math.abs(v) > 1e-9;
    const isUp = v > 0;
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          whiteSpace: "nowrap",
          fontWeight: showDirection ? 600 : "normal",
          ...(showDirection ? { color: isUp ? "#198754" : "#dc3545" } : {}),
        }}
      >
        {showDirection ? (
          <i
            className={`fa-solid ${isUp ? "fa-caret-up" : "fa-caret-down"}`}
            style={{ fontSize: "1.1em", lineHeight: 1 }}
            aria-hidden
          />
        ) : null}
        {`${Math.abs(v).toFixed(2)}%`}
      </span>
    );
  };

  const updateTotals = useCallback((dataObj, miscValue) => {
    const items = Object.values(dataObj || {});
    let totalRs = 0;
    let totalQuantity = 0;

    if (items.length > 0) {
      items.forEach((item) => {
        const priceUi = parseFloat(item?.itemPrice) || 0;
        const quantity = parseFloat(item?.itemQuantity) || 0;
        totalRs += priceUi;
        totalQuantity += quantity;
      });
    }

    const miscellaneousValue = parseFloat(miscValue) || 0;
    totalRs += miscellaneousValue;

    setTotalQty(totalQuantity.toFixed(3));
    setGrandTotalRs(totalRs.toFixed(2));
  }, []);

  /** Map uploadFile from header/items into Details Sheet UI state */
  const applyDetailsSheetFromSource = useCallback((sourceData = {}, sourceItems = []) => {
    let uploadFileRef = sourceData.uploadFile || "";
    if (!uploadFileRef && Array.isArray(sourceItems) && sourceItems.length > 0) {
      const itemWithUpload = sourceItems.find((item) => item?.uploadFile);
      if (itemWithUpload?.uploadFile) {
        uploadFileRef = itemWithUpload.uploadFile;
      }
    }

    if (uploadFileRef) {
      const fileName =
        uploadFileRef.split("/").pop() ||
        uploadFileRef.split("\\").pop() ||
        uploadFileRef;
      setDetailsSheetFileName(fileName);
      setDetailsSheetDownloadUrl(`${API_URL}uploads/iip_manufacturing/${fileName}`);
      setDetailsSheetFileData("");
      setDetailsSheetFileObject(null);
      return true;
    }

    setDetailsSheetFileName("");
    setDetailsSheetDownloadUrl("");
    setDetailsSheetFileData("");
    setDetailsSheetFileObject(null);
    return false;
  }, []);

  const disableButtons = () => {
    const flag = payload.statusFlagSet;
    const { userLevel } = userInfoObject;
    const roleList = Array.isArray(userInfoObject?.roles)
      ? userInfoObject.roles
      : [];
    const isHQAdminOrHQIIP =
      userLevel === "HEAD QUARTER" &&
      (roleList.includes("HQADM") || roleList.includes("HQIIP"));

    // For HQADM/HQIIP roles, keep all actions enabled except statusFlag "8"
    // where existing disabled behavior should remain unchanged.
    if (isHQAdminOrHQIIP && flag !== "8") {
      setIsDeleteRow(false);
      setIsAddRow(false);
      setIsSaveBtnDisable(false);
      setIsSubmitBtnDisable(false);
      setIsRejectBtnDisable(false);
      setIsAcceptBtnDisable(false);
      return;
    }

    var val = "0";
    var val1 = "0";
    if (
      userLevel === "DISTRICT LEVEL" &&
      (flag === "1" ||
        flag === "2" ||
        flag === "3" ||
        flag === "7" ||
        flag === "8")
    ) {
      val = "1";
    }
    if (
      userLevel === "DIVISION LEVEL" &&
      (flag === "0" ||
        flag === "1" ||
        flag === "3" ||
        flag === "6" ||
        flag === "7" ||
        flag === "8")
    ) {
      val = "1";
    }
    if (userLevel === "HEAD QUARTER") {
      if (flag === "0" || flag === "2" || flag === "6" || flag === "8") {
        val = "1";
      }
      if (flag === "1" || flag === "3") {
        val1 = "1";
      }
    }
    if (val === "1") {
      setIsDeleteRow(true);
      setIsAddRow(true);
      setIsSaveBtnDisable(true);
      setIsSubmitBtnDisable(true);
      if (userLevel === "HEAD QUARTER") {
        setIsRejectBtnDisable(true);
        setIsAcceptBtnDisable(true);
      }
      if (userLevel === "DIVISION LEVEL" && flag === "6") {
        setIsSaveBtnDisable(false);
        setIsSubmitBtnDisable(false);
        setIsDeleteRow(false);
      }
      if (userLevel === "DIVISION LEVEL" && flag === "8") {
        setIsSaveBtnDisable(true);
        setIsSubmitBtnDisable(true);
        setIsDeleteRow(true);
      }
    } else {
      setIsDeleteRow(false);
      setIsAddRow(false);
      setIsSaveBtnDisable(false);
      setIsSubmitBtnDisable(false);
    }
    if (val1 === "1") {
      setIsDeleteRow(true);
      setIsAddRow(true);
      setIsSaveBtnDisable(true);
      if (userLevel === "HEAD QUARTER") {
        setIsRejectBtnDisable(false);
        setIsAcceptBtnDisable(false);
      }
    }

    if (status !== "0") {
      enabledisableText();
    }
  };

  const enabledisableText = () => {
    if (districtCode === "00") {
    } else {
    }

    setIsAddRow(false);
  };

  useEffect(() => {
    disableButtons();
    const isStatusClose = status === "0";
    if (isStatusClose) {
      setTotalMenDays("0.00");
      setTotalManuFacturingDays("0.00");
      setTotalProductionValue("0.00");
    } else {
      setTotalMenDays(allManufacturingSectorFromData.totalMenDays);
      setTotalManuFacturingDays(
        allManufacturingSectorFromData.totalManuFacturingDays
      );
      // Value in Thousands — years <= 2025 stored ×1000, show ÷1000
      setTotalProductionValue(
        allManufacturingSectorFromData.totalProductionValue !== null &&
        allManufacturingSectorFromData.totalProductionValue !== undefined &&
        !isZeroNumeric(allManufacturingSectorFromData.totalProductionValue)
          ? formatIipValueByYear(
              allManufacturingSectorFromData.totalProductionValue,
              yearName
            )
          : ""
      );
    }
    seRemarkReason(allManufacturingSectorFromData.remarkReason);
    
    // Map uploadFile from response to Details Sheet field
    let uploadFileName = "";
    let uploadFileUrl = "";
    
    // Check if uploadFile exists in main object
    if (allManufacturingSectorFromData.uploadFile) {
      uploadFileName = allManufacturingSectorFromData.uploadFile;
    } 
    // If not in main object, check in items (first item with uploadFile)
    else if (manufacturedData && manufacturedData.length > 0) {
      const itemWithUploadFile = manufacturedData.find(item => item.uploadFile);
      if (itemWithUploadFile && itemWithUploadFile.uploadFile) {
        uploadFileName = itemWithUploadFile.uploadFile;
      }
    }
    
    // If uploadFile found, construct the download URL
    if (uploadFileName) {
      // Extract filename from path if it's a full path
      const fileName = uploadFileName.split('/').pop() || uploadFileName.split('\\').pop() || uploadFileName;
      setDetailsSheetFileName(fileName);
      // Construct download URL using API_URL with iip_manufacturing path
      uploadFileUrl = `${API_URL}uploads/iip_manufacturing/${fileName}`;
      setDetailsSheetDownloadUrl(uploadFileUrl);
    }
    
    // Also check for existing detailsSheetFileName/detailsSheetFileData/detailsSheetFileUrl
    if (allManufacturingSectorFromData.detailsSheetFileName && !uploadFileName) {
      setDetailsSheetFileName(allManufacturingSectorFromData.detailsSheetFileName);
    }
    if (allManufacturingSectorFromData.detailsSheetFileData) {
      setDetailsSheetFileData(allManufacturingSectorFromData.detailsSheetFileData);
      setDetailsSheetDownloadUrl(allManufacturingSectorFromData.detailsSheetFileData);
    } else if (allManufacturingSectorFromData.detailsSheetFileUrl && !uploadFileUrl) {
      setDetailsSheetDownloadUrl(allManufacturingSectorFromData.detailsSheetFileUrl);
    }

    const initialData = {};
    let otherData;
    manufacturedData.forEach((item, index) => {
      if (item.itemCode === `000000`) {
        otherData = item.itemName
          .replace("Others", "")
          .replace("(", "")
          .replace(")", "")
          .trim();
      } else {
        otherData = "";
      }
      initialData[index] = {
        id: index,
        itemCode: item.itemCode,
        itemName: item.itemName,
        // display value in thousands (with 2 decimal places); <=2025 ÷1000
        itemPrice: formatPriceForState(item.itemPrice, isStatusClose, yearName),
        itemQuantity: formatQuantityForState(item.itemQuantity, isStatusClose),
        reason: item.reason,
        remarkReason: item.remarkReason,
        unitCode: item.unitCode,
        getUnitCodesdata: Object.values(item.getUnitCodesdata),
        itemOtherName: otherData,
        statusFlag: item.statusFlag,
      };
    });
    setItemData(initialData);
    initialRowCountRef.current = manufacturedData?.length ?? 0;

    // Recalculate totals after updating itemData (use same display-scaled misc value)
    const miscForTotals = isStatusClose
      ? "0.00"
      : !isZeroNumeric(initialMiscValue)
        ? formatIipValueByYear(initialMiscValue, yearName)
        : "";
    updateTotals(initialData, miscForTotals);
  }, [manufacturedData, updateTotals, initialMiscValue, status, yearName]);

  useEffect(() => {
    if (status !== "0") return;

    setTotalMenDays("0.00");
    setTotalManuFacturingDays("0.00");
    setTotalProductionValue("0.00");

    setItemData((prev) => {
      const keys = Object.keys(prev || {});
      if (keys.length === 0) return prev;

      const next = {};
      keys.forEach((id) => {
        next[id] = {
          ...prev[id],
          itemQuantity: "0.000",
          itemPrice: "0.00",
        };
      });
      return next;
    });
  }, [status]);

  useEffect(() => {
    updateTotals(itemData, totalProductionValue);
  }, [itemData, totalProductionValue, updateTotals]);

  // handle textBox On change Event
  const handleOnChange = async (indexId, field, newValue) => {
    if (field === "itemPrice") {
      // Allow decimal with max 2 decimal places for price UI
      if (newValue === "" || newValue === ".") {
        // Allow empty or just decimal point for user to type
      } else if (!/^\d*\.?\d{0,2}$/.test(newValue)) {
        toast.error("Please enter a valid number with maximum 2 decimal places.", messages.COLOR_RED);
        return; // keep previous value, do not reset
      }
    } else if (newValue !== "" && !isNumeric(newValue)) {
      toast.error(messages.NUMERIC_MSG, messages.COLOR_RED);
      newValue = "";
    }
    setItemData((prevItemData) => {
      const updatedItemData = {
        ...prevItemData,
        [indexId]: {
          ...prevItemData[indexId],
          [field]: newValue,
        },
      };

      return updatedItemData;
    });
  };

  // handle the add row on add row button  click
  const addRow = () => {
    let newCustomObject = [];
    for (let i = 0; i < 1; i++) {
      const newItemAdded = {
        id: i,
        getUnitCodesdata: [],
        itemCode: "",
        itemName: "",
        itemPrice: "",
        itemQuantity: "",
        reason: "0",
        unitCode: "",
        itemOtherName: "",
        remarkReason: "",
      };
      newCustomObject.push(newItemAdded);
    }
    const dataArray = Object.values(itemData);
    const AddedIndex = dataArray.length;
    dataArray.splice(AddedIndex, 0, ...newCustomObject);
    customObjectObjectData(dataArray);
  };

  // Function to handle update state according to data
  const customObjectObjectData = (ManufacturingSectorData) => {
    const initialData = {};
    // let otherData=''
    ManufacturingSectorData.forEach((item, index) => {
      initialData[index] = {
        id: index,
        itemCode: item?.itemCode || "",
        itemName: item?.itemName || "",
        // keep UI value in thousands
        itemPrice: formatPriceForState(item?.itemPrice),
        itemQuantity: formatQuantityForState(item?.itemQuantity),
        reason: item?.reason || "",
        unitCode: item?.unitCode || "",
        getUnitCodesdata: Object.values(item.getUnitCodesdata || ""),
        itemOtherName: item.itemOtherName,
        remarkReason: item.remarkReason,
      };
    });

    setItemData(initialData);

    // Recalculate totals after updating itemData
    updateTotals(initialData, totalProductionValue);
  };

  // Function to copy previous month data into the table
  const repeatPreviousMonthData = () => {
    const previousMonthHeader = previousMonthData?.[0] || {};
    const previousMonthDetails = (
      previousMonthData?.[0]?.detailsOfItemsManufactured || []
    ).filter(isRepeatablePreviousMonthItem);

    if (!previousMonthDetails.length) {
      toast.error(
        storedLanguage === "hi"
          ? "पिछले माह का डेटा उपलब्ध नहीं है।"
          : "Previous month data not available.",
        messages.COLOR_RED
      );
      return;
    }

    const formattedItems = previousMonthDetails.map((item, index) => {
      const otherData =
        item.itemCode === "000000"
          ? (item.itemName || "")
              .replace("Others", "")
              .replace("(", "")
              .replace(")", "")
              .trim()
          : item.itemOtherName || "";

      return {
        id: index,
        itemCode: item.itemCode,
        itemName: item.itemName,
        itemPrice: formatPriceForState(
          item.itemPrice,
          false,
          previousMonthPayload?.year
        ),
        itemQuantity: formatQuantityForState(item.itemQuantity),
        reason: item.reason || "0",
        unitCode: item.unitCode,
        itemOtherName: otherData,
        remarkReason: item.remarkReason,
        getUnitCodesdata: Object.values(item.getUnitCodesdata || []),
        statusFlag: item.statusFlag,
      };
    });

    const miscDisplay =
      previousMonthHeader.totalProductionValue !== null &&
      previousMonthHeader.totalProductionValue !== undefined &&
      !isZeroNumeric(previousMonthHeader.totalProductionValue)
        ? formatIipValueByYear(
            previousMonthHeader.totalProductionValue,
            previousMonthPayload?.year
          )
        : "";

    if (
      previousMonthHeader.totalMenDays !== undefined &&
      previousMonthHeader.totalMenDays !== null &&
      previousMonthHeader.totalMenDays !== ""
    ) {
      setTotalMenDays(String(previousMonthHeader.totalMenDays));
    }
    if (
      previousMonthHeader.totalManuFacturingDays !== undefined &&
      previousMonthHeader.totalManuFacturingDays !== null &&
      previousMonthHeader.totalManuFacturingDays !== ""
    ) {
      setTotalManuFacturingDays(String(previousMonthHeader.totalManuFacturingDays));
    }
    setTotalProductionValue(miscDisplay);

    customObjectObjectData(formattedItems);

    const dataObj = {};
    formattedItems.forEach((item, idx) => {
      dataObj[idx] = item;
    });
    updateTotals(dataObj, miscDisplay);
    initialRowCountRef.current = formattedItems.length;
    applyDetailsSheetFromSource(previousMonthHeader, previousMonthDetails);

    toast.success(
      storedLanguage === "hi"
        ? "पिछले माह का डेटा सफलतापूर्वक जोड़ा गया। अब Save करें।"
        : "Previous month data copied successfully. You can now Save.",
      messages.COLOR_RED
    );
  };

  //handle Dropdown onChange*********
  const handledropDownOnChange = async (indexId, field, event) => {
    if (field === "itemCode") {
      let selectedIndex = event.target.options.selectedIndex;
      let itemCode = event.target.value;
      let itemName = event.target.options[selectedIndex].text;
      let itemObject = Object.values(itemData).some(
        (item) => item.itemCode === itemCode
      );
      if (itemObject && itemCode !== "000000") {
        alert("Item already selected, Please select different item.");
        return false;
      }

      let unitData = "";
      let defaultUnitCode = "";
      let post_data = {
        itemCode: itemCode,
      };
      showLoading(true);
      try {
        const response = await axios.post(
          `${API_URL}api/iip/itemNameChangeGetUnitData`,
          post_data,
          config
        );
        unitData = response.data.data.getUnitCodesdata;
        defaultUnitCode=response?.data?.data?.unitCodedata || ''
      } catch (error) {
        console.error(error);
      } finally {
        showLoading(false);
      }

      setItemData((prevItemData) => {
        const updatedItemData = {
          ...prevItemData,
          [indexId]: {
            ...prevItemData[indexId],
            itemCode: itemCode,
            itemName: itemName,
            getUnitCodesdata: unitData || "",
            itemOtherName: "",
            unitCode: itemCode !== "000000" ? defaultUnitCode : "",
          },
        };
        return updatedItemData;
      });
    } else {
      let newValue = event.target.value;
      setItemData((prevItemData) => {
        const updatedItemData = {
          ...prevItemData,
          [indexId]: {
            ...prevItemData[indexId],
            [field]: newValue,
          },
        };
        return updatedItemData;
      });
    }
  };

  const handleOtherTextBoxOnChange = (indexId, field, event) => {
    let newValue = event.target.value;
    setItemData((prevItemData) => {
      const updatedItemData = {
        ...prevItemData,
        [indexId]: {
          ...prevItemData[indexId],
          [field]: newValue,
        },
      };
      return updatedItemData;
    });
  };

  const selectItemCodeForRow = useCallback(
    async (indexId, itemCode) => {
      if (!itemCode) return;

      let unitData = "";
      let defaultUnitCode = "";
      const post_data = { itemCode };
      showLoading(true);
      try {
        const response = await axios.post(
          `${API_URL}api/iip/itemNameChangeGetUnitData`,
          post_data,
          config
        );
        unitData = response.data?.data?.getUnitCodesdata || "";
        defaultUnitCode = response?.data?.data?.unitCodedata || "";
      } catch (error) {
        console.error(error);
      } finally {
        showLoading(false);
      }

      const itemNameFromList =
        allItemsList?.find((x) => String(x.itemCode) === String(itemCode))
          ?.itemCodeName || "";

      setItemData((prevItemData) => ({
        ...prevItemData,
        [indexId]: {
          ...prevItemData[indexId],
          itemCode: itemCode,
          itemName: itemNameFromList,
          getUnitCodesdata: unitData,
          itemOtherName: "",
          unitCode: itemCode !== "000000" ? defaultUnitCode : "",
          // Close status requirement: auto-set Qty/Value to 0
          itemQuantity: status === "0" ? "0.000" : prevItemData[indexId]?.itemQuantity,
          itemPrice: status === "0" ? "0.00" : prevItemData[indexId]?.itemPrice,
        },
      }));
    },
    [allItemsList, config, status]
  );

  useEffect(() => {
    // Requirement: Status = Close (0) select hote hi NIC-code items auto-select
    if (status !== "0") {
      autoSelectCloseRef.current = { lastKey: null, inFlight: false };
      return;
    }

    const nicItems = (allItemsList || [])
      .filter((x) => x && x.itemCode && String(x.itemCode).trim() !== "")
      .filter((x) => String(x.itemCode) !== "000000");

    if (nicItems.length === 0) return;

    const key = `${industryCode || ""}|${districtCode || ""}|${yearName || ""}|${monthName || ""}|close`;
    if (autoSelectCloseRef.current.inFlight) return;
    if (autoSelectCloseRef.current.lastKey === key) return;

    const currentRows = Object.values(itemData || []);
    if (currentRows.length === 0) return;

    autoSelectCloseRef.current.inFlight = true;
    autoSelectCloseRef.current.lastKey = key;

    const desiredCount = Math.min(nicItems.length, 10);

    // Ensure enough rows exist
    if (currentRows.length < desiredCount) {
      const extended = [...currentRows];
      for (let i = extended.length; i < desiredCount; i += 1) {
        extended.push({
          id: i,
          getUnitCodesdata: [],
          itemCode: "",
          itemName: "",
          itemPrice: "",
          itemQuantity: "",
          reason: "0",
          unitCode: "",
          itemOtherName: "",
          remarkReason: "",
        });
      }
      customObjectObjectData(extended);
    }

    (async () => {
      try {
        for (let i = 0; i < desiredCount; i += 1) {
          const rowId = i;
          const itemCode = nicItems[i]?.itemCode;
          await selectItemCodeForRow(rowId, itemCode);
        }
      } finally {
        autoSelectCloseRef.current.inFlight = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, allItemsList, industryCode, districtCode, yearName, monthName]);

  // handle Save data
  const handleManufacturingSectorData = async (e, buttonType) => {
    let month = monthName;
    let year = yearName;
    const isSaveForNextMonth = buttonType === "SAVE FOR NEXT MONTH" ? 1 : 0;
    if (buttonType === "SAVE FOR NEXT MONTH") {
      const currentMonthIndex = parseInt(shortMonthToMonthNumber(month));
      if (currentMonthIndex > 11) {
        month = 1;
        year = parseInt(year) + 1;
      } else {
        month = currentMonthIndex + 1;
      }
      month = getMonthNumberToName(month);
      buttonType = "SAVE";
    }

    let clickButtonStatusFlag = getStatusFlagValue(
      buttonType,
      userInfoObject.userLevel
    ); //button click status flag
    const customItemCodes = [];
    let check = 0;
    let localbodyCodeCheck = 0;
    let statusFlag = "";
    let isRejectStatus = false;
    let serialNumber = "";
    let industryItemStatus = "1";
    let no = 0;
    let otherId = -1;
    let newItemCode = "";
    let industryCodeData = industryList.filter(
      (item, index) => item.industrialUnitCode === industryCode
    );

    if (status === "") {
      toast.error("Please Select Status.", messages.COLOR_RED);
      return false;
    }

    // Skip validations if status is Close (0)
    const isStatusClose = status === "0";

    const menDaysNum = parseFloat(totalMenDays);
    const mfgDaysNum = parseFloat(totalManuFacturingDays);
    const isZeroManAndMfgDays =
      totalMenDays !== "" &&
      totalManuFacturingDays !== "" &&
      !Number.isNaN(menDaysNum) &&
      !Number.isNaN(mfgDaysNum) &&
      menDaysNum === 0 &&
      mfgDaysNum === 0;

    if (!isStatusClose) {
      // When status is open, Details Sheet file is mandatory
      const hasDetailsSheet = detailsSheetFileName || detailsSheetFileData || detailsSheetDownloadUrl;
      if (!hasDetailsSheet && !isHQIIPHeadQuarter) {
        toast.error("Please upload Details Sheet file", messages.COLOR_RED);
        return false;
      }
      if (!checkTotalManDays1(totalMenDays)) {
        return false;
      }
      //Total Manufacturing Days Validation
      if (!checkTotalMfgDays1(totalManuFacturingDays, totalMenDays)) {
        return false;
      }

      const dataArray = Object.values(itemData);
      const allRowsIdle =
        dataArray.length === 0 ||
        dataArray.every((row) => isIdleManufacturingRow(row));

      if (dataArray.length > 0 && !(isZeroManAndMfgDays && allRowsIdle)) {
        let price = 0;
        let quantity = 0;

        for (let i = 0; i < dataArray.length; i++) {
          const uiPrice = parseFloat(dataArray[i].itemPrice) || 0;
          price += uiPrice;
          quantity += parseFloat(dataArray[i].itemQuantity) || 0;
        }

        const totalQtyNum = parseFloat(totalQty);
        const normalizedTotalQty = Number.isNaN(totalQtyNum) ? 0 : totalQtyNum;
        if (quantity.toFixed(3) !== normalizedTotalQty.toFixed(3)) {
          toast.error(
            "Total Quantity does not match with sum of all quantities.",
            messages.COLOR_RED
          );
          return false;
        }

        const miscellaneousValue = parseFloat(totalProductionValue) || 0;
        const expectedGrandTotal = parseFloat((price + miscellaneousValue).toFixed(2));
        const actualGrandTotalRaw = parseFloat(grandTotalRs);
        const actualGrandTotal = Number.isNaN(actualGrandTotalRaw)
          ? 0
          : actualGrandTotalRaw;

        if (expectedGrandTotal !== actualGrandTotal) {
          toast.error(
            "Grand Total Does not match with total of Values.",
            messages.COLOR_RED
          );
          return false;
        }
      }
    }

    const previousMonthItemsList =
      previousMonthData?.[0]?.detailsOfItemsManufactured || [];

    for (let id in itemData) {
      industryItemStatus = "1";
      if (itemData[id].itemCode === "000000") {
        industryItemStatus = "0";
        newItemCode = `00000${++otherId}`;
        if (!isStatusClose && itemData[id].itemOtherName === "") {
          toast.error("Please enter other value.", messages.COLOR_RED);
          return false;
        }
      } else {
        newItemCode = itemData[id].itemCode;
      }

      const skipItemRowRequired =
        isZeroManAndMfgDays && isIdleManufacturingRow(itemData[id]);

      if (
        !isStatusClose &&
        !skipItemRowRequired &&
        itemData[id].itemCode !== "000000" &&
        itemData[id].unitCode === ""
      ) {
        if (!itemData[id].itemCode || String(itemData[id].itemCode).trim() === "") {
          toast.error("Please select Item Name", messages.COLOR_RED);
          return false;
        }
        toast.error("Please Select Unit.", messages.COLOR_RED);
        return false;
      }

      let itemOtherName = itemData[id].itemOtherName;
      serialNumber = ++no;
      let itemName = itemData[id].itemName;
      let unitCode = itemData[id].unitCode;
      let itemQuantity = itemData[id].itemQuantity || "";
      let itemPrice =
        itemData[id].itemPrice !== "" && itemData[id].itemPrice !== undefined
          ? toIipStorageValueByYear(itemData[id].itemPrice, yearName)
          : "";
      let reason = itemData[id].reason;
      let itemCode = newItemCode;
      statusFlag =
        itemData[id].statusFlag === ""
          ? clickButtonStatusFlag
          : itemData[id].statusFlag; //database Statusflag

      let requiresReasonDueToVariation = false;
      if (
        !isStatusClose &&
        previousMonthItemsList.length > 0 &&
        itemData[id].itemCode &&
        itemData[id].itemCode !== "000000"
      ) {
        const prevMonthItem = previousMonthItemsList.find(
          (prevItem) => prevItem.itemCode === itemData[id].itemCode
        );

        if (prevMonthItem) {
          const prevQty = parseFloat(prevMonthItem.itemQuantity) || 0;
          const currentQty = parseFloat(itemQuantity) || 0;

          let variationPercent = 0;
          if (prevQty === 0) {
            variationPercent = currentQty !== 0 ? 100 : 0;
          } else {
            variationPercent = ((currentQty - prevQty) / prevQty) * 100;
          }

          if (Math.abs(variationPercent) > 10) {
            requiresReasonDueToVariation = true;
          }
        }
      }

      if (
        !isStatusClose &&
        requiresReasonDueToVariation &&
        (!reason || reason === "0" || reason.trim() === "")
      ) {
        toast.error(
          storedLanguage === "hi"
            ? "मात्रा में 10% से अधिक परिवर्तन के लिए कृपया कारण चुनें।"
            : "Please select the reason because quantity variation is greater than 10%.",
          messages.COLOR_RED
        );
        return false;
      }

      if (
        buttonType === "ACCEPT" ||
        buttonType === "SAVE" ||
        buttonType === "SUBMIT" ||
        buttonType === "REJECT"
      ) {
        //Accept Button click
        statusFlag = clickButtonStatusFlag;
      }

      if (buttonType === "REJECT" && remarkReason === "") {
        toast.error("Please fill Remark for rejection.", messages.COLOR_RED);
        return false;
      }

      const customObject = {
        itemName: itemName,
        unitCode: unitCode,
        itemQuantity: itemQuantity,
        itemPrice: itemPrice,
        reason: reason,
        itemCode: itemCode,
        statusFlag: statusFlag,
        serialNumber: serialNumber,
        industryItemStatus: industryItemStatus,
        itemOtherName: itemOtherName,
      };
      customItemCodes.push(customObject);
    }

    // Create FormData for file upload
    const formData = new FormData();
    
    // Add all form fields to FormData
    formData.append('industrialUnitCode', industryCode);
    formData.append('address', address);
    formData.append('districtCode', districtCode);
    formData.append('industrialUnitName', industryCodeData[0].industrialUnitName);
    formData.append('totalMenDays', totalMenDays === "" ? "0.00" : totalMenDays);
    formData.append('totalManuFacturingDays', totalManuFacturingDays === "" ? "0.00" : totalManuFacturingDays);
    formData.append('totalProductionValue', totalProductionValue && totalProductionValue.trim() !== "" 
      ? toIipStorageValueByYear(totalProductionValue, yearName) 
      : "0.00");
    formData.append('remarkReason', remarkReason);
    formData.append('status', status);
    formData.append('itemCodes', JSON.stringify(customItemCodes));
    formData.append('statusFlag', statusFlag);
    formData.append('payload', JSON.stringify({
      month: month,
      year: year,
      buttonType: buttonType,
      statusButtonFlag: clickButtonStatusFlag,
      saveForNextMonth: isSaveForNextMonth,
    }));
    
    // Handle file upload - use stored file object if available, otherwise convert from base64
    if (detailsSheetFileName) {
      if (detailsSheetFileObject) {
        // Use stored file object (preferred method)
        formData.append('uploadFile', detailsSheetFileObject);
      } else if (detailsSheetFileData) {
        // Convert base64 to File object if file object not available
        try {
          // Extract base64 data (remove data URL prefix if present)
          const base64Data = detailsSheetFileData.includes(',') 
            ? detailsSheetFileData.split(',')[1] 
            : detailsSheetFileData;
          
          // Determine MIME type from file extension
          const ext = detailsSheetFileName.split('.').pop()?.toLowerCase();
          let mimeType = 'application/octet-stream';
          if (ext === 'pdf') mimeType = 'application/pdf';
          else if (ext === 'xls') mimeType = 'application/vnd.ms-excel';
          else if (ext === 'xlsx') mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          
          // Convert base64 to blob
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: mimeType });
          
          // Create File object from blob
          const file = new File([blob], detailsSheetFileName, { type: mimeType });
          
          // Append file to FormData
          formData.append('uploadFile', file);
        } catch (error) {
          console.error('Error converting base64 to file:', error);
          // If conversion fails, send filename only
          formData.append('uploadFile', detailsSheetFileName);
        }
      } else {
        // Only filename available (from existing data)
        formData.append('uploadFile', detailsSheetFileName);
      }
    }

    // Create config with multipart/form-data content type
    const formDataConfig = {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data',
      },
    };

    showLoading(true);
    let isSaveSuccess = false;
    try {
      const response = await axios.post(
        `${API_URL}api/iip/IIPMonthlyProductionManufacturingSectorSaveDB`,
        formData,
        formDataConfig
      );
      if (response.data.status) {
        isSaveSuccess = true;
        toast.success(response.data.message, messages.COLOR_RED);
      } else {
        const errMsg =
          response.data?.message ||
          "An error occurred while processing your request.";
        toast.error(errMsg, messages.COLOR_RED);
      }
    } catch (error) {
      console.error(error);
      const errMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "An error occurred while processing your request.";
      toast.error(errMsg, messages.COLOR_RED);
    } finally {
      showLoading(false);
      if (isSaveSuccess && isHQAdminOrHQIIPHeadQuarter && onSaveSuccessRefresh) {
        await onSaveSuccessRefresh();
      } else {
        resetForm();
      }
    }
  };

  // Total Men days Validation
  const checkTotalManDays1 = (days) => {
    if (districtCode !== "00") {
      if (days === "") {
        toast.error("Enter Total man days.", messages.COLOR_RED);
        return false;
      }
      const parsedDays = parseFloat(days);
      if (isNaN(parsedDays)) {
        toast.error("Total man days are not valid.", messages.COLOR_RED);
        return false;
      }
      if (parsedDays < 0) {
        toast.error("Total man days are not valid.", messages.COLOR_RED);
        return false;
      }
    }
    return true;
  };

  // Total Manufacturing Days Validation
  function checkTotalMfgDays1(days, menDays) {
    if (districtCode !== "00") {
      if (days === "") {
        toast.error("Enter Total manufacturing days.", messages.COLOR_RED);
        return false;
      }
      const parsedMfgDays = parseFloat(days);
      if (isNaN(parsedMfgDays)) {
        toast.error(
          "Total manufacturing days are not valid.",
          messages.COLOR_RED
        );
        return false;
      }
      if (parsedMfgDays < 0) {
        toast.error(
          "Total manufacturing days are not valid.",
          messages.COLOR_RED
        );
        return false;
      }

      if (menDays === "") {
        return true;
      } else if (parseFloat(menDays) < parseFloat(days)) {
        toast.error(
          "Total Manufacturing days cannot be greater than Total Man days.",
          messages.COLOR_RED
        );
        return false;
      }
    }
    return true;
  }

  //Reset Form data
  const resetForm = () => {
    resetFormData();
  };

  const handleTextBoxOnChange = (e) => {
    let { name, value } = e.target;
    if (
      !isNumeric(value) &&
      (name === "totalMenDays" || name === "totalManuFacturingDays")
    ) {
      toast.error(messages.NUMERIC_MSG, messages.COLOR_RED);
      value = "";
    }

    if (name === "totalMenDays") {
      setTotalMenDays(value);
    } else if (name === "totalManuFacturingDays") {
      setTotalManuFacturingDays(value);
    } else if (name === "totalProductionValue") {
      // Allow decimal with max 2 decimal places for Other Miscellaneous Items
      if (value === "" || value === ".") {
        setTotalProductionValue(value);
        updateTotals(itemData, value);
      } else if (!/^\d*\.?\d{0,2}$/.test(value)) {
        toast.error("Please enter a valid number with maximum 2 decimal places.", messages.COLOR_RED);
        return; 
      } else {
        setTotalProductionValue(value);
        updateTotals(itemData, value);
      }
    } else if (name === "remarkReason") {
      seRemarkReason(value);
    }
  };

  const handleDetailsSheetFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setDetailsSheetFileName("");
      setDetailsSheetFileData("");
      setDetailsSheetDownloadUrl("");
      setDetailsSheetFileObject(null);
      return;
    }

    const result = validateFileForUpload(file, ['.pdf', '.xls', '.xlsx'], {
      maxSize: 2 * 1024 * 1024,
      sizeErrorMsg: 'File should be up to 2MB',
    });
    if (!result.valid) {
      toast.error(result.error, messages.COLOR_RED);
      e.target.value = "";
      setDetailsSheetFileName("");
      setDetailsSheetFileData("");
      setDetailsSheetDownloadUrl("");
      setDetailsSheetFileObject(null);
      return;
    }

    // For Excel files (xls, xlsx), only extension and size validation is done
    // For PDF files, optional magic number validation can be added if needed
    // Store file object and read as base64 for display
    setDetailsSheetFileObject(file); // Store original file object for upload
    const dataReader = new FileReader();
    dataReader.onload = (ev) => {
      const result = ev.target?.result;
      if (typeof result === "string") {
        setDetailsSheetFileName(file.name);
        setDetailsSheetFileData(result);
        setDetailsSheetDownloadUrl(result);
      }
    };
    dataReader.onerror = () => {
      toast.error("Error reading file. Please try again.", messages.COLOR_RED);
      e.target.value = "";
      setDetailsSheetFileName("");
      setDetailsSheetFileData("");
      setDetailsSheetDownloadUrl("");
      setDetailsSheetFileObject(null);
    };
    dataReader.readAsDataURL(file);
  };

  const handleDetailsSheetDownload = () => {
    if (!detailsSheetDownloadUrl) {
      return;
    }
    const link = document.createElement("a");
    link.href = detailsSheetDownloadUrl;
    link.target = "_blank";
    link.rel = "noreferrer";
    // If it's a URL (not base64 data), add download attribute
    if (detailsSheetDownloadUrl.startsWith("http") || detailsSheetDownloadUrl.startsWith(API_URL)) {
      link.download = detailsSheetFileName || "details-sheet";
    }
    link.click();
  };

  const handleProductionBlur = () => {
    if (totalProductionValue && totalProductionValue.trim() !== "") {
      const num = parseFloat(totalProductionValue);
      if (!isNaN(num)) {
        setTotalProductionValue(num === 0 ? "0.00" : num.toFixed(2));
      } else {
        setTotalProductionValue("");
      }
    }
  };

  const formatToTwoDecimals = (val) => {
    if (val === "") return "";
    const num = parseFloat(val);
    return isNaN(num) ? "" : num.toFixed(2);
  };

  // Function to handle delete row - deletes only rows whose Reason-column checkbox is checked
  const deleteRows = () => {
    const dataArray = Object.values(itemData);
    const checkedIds = dataArray
      .filter((item) => reasonRowCheckbox[item.id])
      .map((item) => item.id);

    if (checkedIds.length === 0) {
      toast.error(`Please check the checkbox of row(s) to delete`, messages.COLOR_RED);
      return false;
    }

    const updatedItems = dataArray.filter((item) => !reasonRowCheckbox[item.id]);
    if (updatedItems.length === 0) {
      toast.error(`At least one row must remain`, messages.COLOR_RED);
      return false;
    }

    const confirmMsg = checkedIds.length === 1
      ? "Are you sure you want to delete the selected row?"
      : `Are you sure you want to delete ${checkedIds.length} selected rows?`;
    if (!window.confirm(confirmMsg)) {
      return;
    }

    customObjectObjectData(updatedItems);
    setReasonRowCheckbox({});
  };

  // Function to handle the store chckbox checked data
  const handleCheckboxChange = (event, index) => {
    if (event.target.checked) {
      setCheckedItems([...checkedItems, index]);
    } else {
      setCheckedItems(checkedItems.filter((item) => item !== index));
    }
  };

  const totalOnChange = (e) => {
    let { name, value } = e.target;

    if (name === "totalQty") {
      if (!isNumeric(value)) {
        toast.error(messages.NUMERIC_MSG, messages.COLOR_RED);
        return;
      }
      setTotalQty(value);
    }
    if (name === "grandTotalRs") {
      if (!isNumeric(value)) {
        toast.error(messages.NUMERIC_MSG, messages.COLOR_RED);
        return;
      }
      setGrandTotalRs(value);
    }
  };


  return (
    <>
      {/* <ToastContainer /> */}
      <div className="card-body">
        <div style={{ display: "flex", gap: "20px" }}>
          <div style={{ width: "100%" }}>
            <div>
              {storedLanguage === "hi"
                ? iipTitleData.hi.DESCRIPTOIN
                : iipTitleData.en.DESCRIPTOIN}
            </div>
            <table
              className="table table-bordered"
              style={{ height: "fit-content" }}
            >
              <thead>
                <tr>
                  <th>
                    {storedLanguage === "hi"
                      ? iipTitleData.hi.TOTAL_MAN_DAYS
                      : iipTitleData.en.TOTAL_MAN_DAYS}
                  </th>
                  <th>
                    {storedLanguage === "hi"
                      ? iipTitleData.hi.TOTAL_MANUFACTURING_DAYS
                      : iipTitleData.en.TOTAL_MANUFACTURING_DAYS}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      name="totalMenDays"
                      id="totalMenDays"
                      maxLength="10"
                      onChange={(e) => handleTextBoxOnChange(e)}
                      value={status === "0" ? "0.00" : totalMenDays}
                      disabled={status === "0"}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      name="totalManuFacturingDays"
                      id="totalManuFacturingDays"
                      maxLength="5"
                      onChange={(e) => handleTextBoxOnChange(e)}
                      value={status === "0" ? "0.00" : totalManuFacturingDays}
                      disabled={status === "0"}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
        </div>

        <div
          style={
            isCfSource && status !== "0"
              ? {
                  backgroundColor: "#fff9c4",
                  padding: "10px",
                  borderRadius: "6px",
                  marginBottom: "8px",
                }
              : undefined
          }
        >
          {storedLanguage === "hi"
            ? iipTitleData.hi.DETAILS_OF_ITEMS_MANUFACTURED
            : iipTitleData.en.DETAILS_OF_ITEMS_MANUFACTURED}
          <table
            className="table table-bordered"
            style={isCfSource && status !== "0" ? { backgroundColor: "#fff9c4" } : undefined}
          >
          <thead>
            <tr>
              <th>
                {storedLanguage === "hi"
                  ? othersData.hi.SERIAL_NO
                  : othersData.en.SERIAL_NO}
              </th>
              <th>
                {storedLanguage === "hi"
                  ? othersData.hi.ITEM_NAME
                  : othersData.en.ITEM_NAME}
              </th>
              <th>
                {storedLanguage === "hi"
                  ? titlesData.hi.UNIT
                  : titlesData.en.UNIT}
              </th>
              <th>
                {storedLanguage === "hi"
                  ? iipTitleData.hi.QUANTITY
                  : iipTitleData.en.QUANTITY}
              </th>
              <th>
                {storedLanguage === "hi"
                  ? iipTitleData.hi.VALUE_IN_RS
                  : iipTitleData.en.VALUE_IN_RS}
              </th>
              {previousYearMonthPayload && previousYearMonthPayload.month && (
                <th>
                  {(() => {
                    const monthName = iipTitleData[storedLanguage]?.MONTHS?.[
                      previousYearMonthPayload.month
                    ] || previousYearMonthPayload.month;
                    const shortMonth = typeof monthName === 'string' 
                      ? monthName.substring(0, 3).charAt(0).toUpperCase() + monthName.substring(1, 3).toLowerCase()
                      : monthName;
                    const shortYear = String(previousYearMonthPayload.year).slice(-2);
                    return `${shortMonth},${shortYear} ${storedLanguage === "hi" ? iipTitleData.hi.VARIATION : iipTitleData.en.VARIATION}`;
                  })()}
                </th>
              )}
              {previousMonthPayload && previousMonthPayload.month && (
                <th>
                  {(() => {
                    const monthName = iipTitleData[storedLanguage]?.MONTHS?.[
                      previousMonthPayload.month
                    ] || previousMonthPayload.month;
                    const shortMonth = typeof monthName === 'string' 
                      ? monthName.substring(0, 3).charAt(0).toUpperCase() + monthName.substring(1, 3).toLowerCase()
                      : monthName;
                    const shortYear = String(previousMonthPayload.year).slice(-2);
                    return `${shortMonth},${shortYear} ${storedLanguage === "hi" ? iipTitleData.hi.VARIATION : iipTitleData.en.VARIATION}`;
                  })()}
                </th>
              )}
              <th>
                {storedLanguage === "hi"
                  ? iipTitleData.hi.REASON
                  : iipTitleData.en.REASON}
              </th>
              <th style={{ width: "44px" }}></th>
            </tr>
          </thead>
          <tbody>
            {Object.values(itemData)?.map((item, i) => {
              srNo = i;
              let otherData = "";
              if (itemData[item.id]?.itemCode === `000000`) {
                otherData = itemData[item.id]?.itemName
                  .replace("Others", "")
                  .replace("(", "")
                  .replace(")", "");
              }
              const isStatusClose = status === "0";
              // Close case: all NIC rows should stay disabled
              let isDisable = isStatusClose;
              const currentItemCode = itemData[item.id]?.itemCode;
              // Disable Unit, Quantity, Value, Reason, checkbox until Item Name is selected
              const hasItemNameSelected = !!(currentItemCode && String(currentItemCode).trim());
              const isRowFieldsDisabled = isDisable || !hasItemNameSelected;

              // Find matching items from previous data for variation calculation
              const currentQty = parseFloat(itemData[item.id]?.itemQuantity) || 0;
              
              // Find previous year month item by itemCode
              const previousYearItem = previousYearMonthData?.[0]?.detailsOfItemsManufactured?.find(
                (prevItem) => prevItem.itemCode === currentItemCode
              );
              
              // Find previous month item by itemCode
              const previousMonthItem = previousMonthData?.[0]?.detailsOfItemsManufactured?.find(
                (prevItem) => prevItem.itemCode === currentItemCode
              );

              // Calculate variation percentage for previousYearMonth
              const previousYearVariation = previousYearItem && currentItemCode
                ? (() => {
                    const previousQty = parseFloat(previousYearItem.itemQuantity) || 0;
                    const qtyPercentage = previousQty !== 0 
                      ? ((currentQty - previousQty) / previousQty) * 100 
                      : (currentQty !== 0 ? 100 : 0);
                    return qtyPercentage;
                  })()
                : null;

              // Calculate variation percentage for previousMonth
              const previousMonthVariation = previousMonthItem && currentItemCode
                ? (() => {
                    const previousQty = parseFloat(previousMonthItem.itemQuantity) || 0;
                    const qtyPercentage = previousQty !== 0 
                      ? ((currentQty - previousQty) / previousQty) * 100 
                      : (currentQty !== 0 ? 100 : 0);
                    return qtyPercentage;
                  })()
                : null;

              const requiresReason =
                !isStatusClose &&
                previousMonthVariation !== null &&
                Math.abs(previousMonthVariation) > 10;
              const showReasonError =
                requiresReason && (!itemData[item.id]?.reason || itemData[item.id]?.reason === "0");

              const isHighVariation =
                !isStatusClose &&
                previousMonthVariation !== null &&
                Math.abs(previousMonthVariation) > 10;

              return (
                <tr
                  key={String(item.id)}
                  style={
                    isHighVariation
                      ? { backgroundColor: "#fff3cd" } // light warning highlight for ±10% variation
                      : undefined
                  }
                >
                  <td>{++srNo}</td>
                  <td>
                    <select
                      name={`itemCode${i}`}
                      id={`itemCode${i}`}
                      className="form-select"
                      disabled={isDisable}
                      onChange={(e) =>
                        handledropDownOnChange(item.id, "itemCode", e)
                      }
                      value={itemData[item.id]?.itemCode}
                    >
                      <option value="" disabled>
                        {" "}
                        Select Status
                      </option>
                      {(() => {
                        const selectedInOtherRows = Object.entries(itemData || {})
                          .filter(([id]) => String(id) !== String(item.id))
                          .map(([, d]) => d?.itemCode)
                          .filter((code) => code != null && code !== "");
                        return allItemsList
                          .filter(
                            (singleItem) =>
                              singleItem.itemCode === "000000" ||
                              !selectedInOtherRows.includes(singleItem.itemCode)
                          )
                          .map((singleItem, index) => (
                            <option value={singleItem.itemCode} key={singleItem.itemCode || index}>
                              {" "}
                              {singleItem.itemCodeName}
                            </option>
                          ));
                      })()}
                    </select>

                    {itemData[item.id]?.itemCode === `000000` && (
                      <input
                        type="text"
                        className="form-control"
                        id={`other${i}`}
                        name={`other${i}`}
                        maxLength="105"
                        disabled={isRowFieldsDisabled}
                        value={itemData[item.id].itemOtherName}
                        onChange={(e) =>
                          handleOtherTextBoxOnChange(
                            item.id,
                            "itemOtherName",
                            e
                          )
                        }
                      />
                    )}
                  </td>

                  <td>
                    <select
                      id={`unitCode${i}`}
                      name={`unitCode${i}`}
                      className="form-select"
                      disabled={
                        isRowFieldsDisabled ||
                        (!allowUnitDropdownForHQBeforeApr2026 &&
                          itemData[item.id]?.itemCode !== "000000")
                      }
                      value={itemData[item.id]?.unitCode}
                      onChange={(e) =>
                        handledropDownOnChange(item.id, "unitCode", e)
                      }
                    >
                      <option value="" disabled>
                        {" "}
                        Select Status
                      </option>
                      {item.getUnitCodesdata.map((unit, index) => {
                        return (
                          <option value={unit.unitCode} key={index}>
                            {" "}
                            {unit.unitName}
                          </option>
                        );
                      })}
                    </select>
                  </td>

                  <td>
                    <input
                      type="text"
                      className="form-control"
                      id={`itemQuantity${i}`}
                      name={`itemQuantity${i}`}
                      maxLength="15"
                      disabled={isRowFieldsDisabled}
                      value={
                        isStatusClose
                          ? "0.000"
                          : getInputDisplayValue(itemData[item.id]?.itemQuantity)
                      }
                      onChange={(e) =>
                        handleOnChange(item.id, "itemQuantity", e.target.value)
                      }
                    />
                  </td>

                  <td>
                    <input
                      type="text"
                      className="form-control"
                      id={`itemPrice${i}`}
                      name={`itemPrice${i}`}
                      maxLength="15"
                      disabled={isRowFieldsDisabled}
                      value={
                        isStatusClose
                          ? "0.00"
                          : getInputDisplayValue(itemData[item.id]?.itemPrice)
                      }
                      onChange={(e) =>
                        handleOnChange(item.id, "itemPrice", e.target.value)
                      }
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && value.trim() !== "" && value !== ".") {
                          const num = parseFloat(value);
                          if (!isNaN(num)) {
                            setItemData((prevItemData) => {
                              const updatedItemData = {
                                ...prevItemData,
                                [item.id]: {
                                  ...prevItemData[item.id],
                                  itemPrice: num.toFixed(2),
                                },
                              };
                              return updatedItemData;
                            });
                          }
                        }
                      }}
                    />
                  </td>
                  {previousYearMonthPayload && previousYearMonthPayload.month && (
                    <td>
                      {previousYearVariation !== null
                        ? renderQtyVariationPercent(previousYearVariation)
                        : null}
                    </td>
                  )}
                  {previousMonthPayload && previousMonthPayload.month && (
                    <td>
                      {previousMonthVariation !== null
                        ? renderQtyVariationPercent(previousMonthVariation)
                        : null}
                    </td>
                  )}
                  <td>
                    <select
                      className="form-select"
                      id={`reason${i}`}
                      name={`reason${i}`}
                      disabled={isRowFieldsDisabled}
                      value={itemData[item.id]?.reason}
                      onChange={(e) =>
                        handledropDownOnChange(item.id, "reason", e)
                      }
                    >
                      <option value="0"> [-- Select --] </option>
                      <option value="1">Shut down in factory</option>
                      <option value="2">
                        Decrease / increase in value of goods for the product of
                        production
                      </option>
                      <option value="3">
                        Material not available for produce item
                      </option>
                      <option value="4">
                        Do not produce due to repair of machines
                      </option>
                      <option value="5">Strike in factory</option>
                      <option value="99">Other</option>
                    </select>
                    {showReasonError && (
                      <div
                        className="text-danger"
                        style={{ fontSize: "11px", marginTop: "4px" }}
                      >
                        {storedLanguage === "hi"
                          ? "कृपया कारण चुनें (मात्रा में 10% से अधिक परिवर्तन)."
                          : "Please select the reason (quantity variation is greater than 10%)."}
                      </div>
                    )}
                  </td>
                  <td className="text-center align-middle">
                    {((initialRowCountRef.current != null && item.id >= initialRowCountRef.current) || itemData[item.id]?.itemCode === '000000') ? (
                      <input
                        type="checkbox"
                        id={`reasonCheckbox-${item.id}`}
                        checked={reasonRowCheckbox[item.id] || false}
                        onChange={(e) => {
                          setReasonRowCheckbox((prev) => ({
                            ...prev,
                            [item.id]: e.target.checked,
                          }));
                        }}
                        disabled={isDisable}
                      />
                    ) : null}
                  </td>
                </tr>
              );
            })}

            <tr>
              <td colSpan={5 + (previousYearMonthPayload && previousYearMonthPayload.month ? 1 : 0) + (previousMonthPayload && previousMonthPayload.month ? 1 : 0)}>
                {storedLanguage === "hi"
                  ? iipTitleData.hi.OTHER_MISCELLANEOUS_ITEMS
                  : iipTitleData.en.OTHER_MISCELLANEOUS_ITEMS}
              </td>

              <td>
                <input
                  type="text"
                  className="form-control"
                  name="totalProductionValue"
                  id="totalProductionValue"
                  maxLength="20"
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow decimal with max 2 decimal places - same as itemPrice
                    if (value === "" || value === "." || /^\d*\.?\d{0,2}$/.test(value)) {
                      handleTextBoxOnChange(e);
                    } else {
                      toast.error("Please enter a valid number with maximum 2 decimal places.", messages.COLOR_RED);
                    }
                  }}
                  onBlur={handleProductionBlur}
                  readOnly={
                    status === "0" ||
                    ((totalMenDays === "" ||
                      totalMenDays === null ||
                      totalMenDays === undefined) &&
                      parseFloat(totalProductionValue || 0) === 0)
                  }
                  disabled={status === "0"}
                  value={status === "0" ? "0.00" : totalProductionValue}
                />
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
        </div>

        <div className="row">
          <div className="col-md-6">
            <label>
              {" "}
              {storedLanguage === "hi"
                ? iipTitleData.hi.TOTAL_QUANTITY
                : iipTitleData.en.TOTAL_QUANTITY}{" "}
            </label>
            <input
              type="text"
              className="form-control"
              name="totalQty"
              id="totalQty"
              maxLength="12"
              value={status === "0" ? "0.000" : parseFloat(totalQty) !== 0 ? totalQty : ""}
              disabled={status === "0"}
              onChange={totalOnChange}
            />
          </div>

          <div className="col-md-6">
            <label>
              {" "}
              {storedLanguage === "hi"
                ? iipTitleData.hi.GRAND_TOTAL
                : iipTitleData.en.GRAND_TOTAL}{" "}
            </label>
            <input
              type="text"
              className="form-control"
              name="grandTotalRs"
              id="grandTotalRs"
              maxLength="12"
              value={status === "0" ? "0.00" : parseFloat(grandTotalRs) !== 0 ? grandTotalRs : ""}
              disabled={status === "0"}
              onChange={totalOnChange}
            />
          </div>
        </div>

        {messageLogData.length > 0 && (
          <>
            <br />
            Message Log
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Sr.No</th>
                  <th>Message By</th>
                  <th>Message</th>
                  <th>Action</th>
                  <th>Date</th>
                </tr>
              </thead>

              <tbody>
                {messageLogData.map((log, index) => {
                  const date = new Date(log.date);
                  const formattedDate = `${date.getDate()}-${date.getMonth() + 1
                    }-${date.getFullYear()}`;
                  return (
                    <tr
                      key={`${log.messageBy ?? ""}-${log.date ?? ""}-${log.action ?? ""}-${index}`}
                    >
                      <td>{index + 1}</td>
                      <td>{log.messageBy}</td>
                      <td>{log.message}</td>
                      <td>{log.action}</td>
                      <td>{formattedDate}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}

        <br />
        <div className="row">
          <div className="col-md-6">
            <label>Remark</label>
            <textarea
              type="text"
              className="form-control"
              name="remarkReason"
              id="remarkReason"
              onChange={(e) => handleTextBoxOnChange(e)}
              value={remarkReason}
            />
          </div>
          <div className="col-md-6">
            <label>
              Details Sheet (File Upload)
              {status !== "0" && !isHQIIPHeadQuarter && <span className="text-danger ms-1">*</span>}
            </label>
            <input
              type="file"
              className="form-control"
              accept=".pdf,.xls,.xlsx"
              onChange={handleDetailsSheetFileChange}
              disabled={status === "0"}
              required={status !== "0" && !isHQIIPHeadQuarter}
            />
            {detailsSheetFileName && (
              <small className="text-muted d-block mt-1">{detailsSheetFileName}</small>
            )}
            <button
              type="button"
              className="btn btn-sm btn-success mt-2"
              onClick={handleDetailsSheetDownload}
              disabled={!detailsSheetDownloadUrl || status === "0"}
            >
              Download
            </button>
          </div>
        </div>

        <br />
        <p>Prevoius Year and Previous Month Data</p>
        
        {/* Single merged table for both months */}
        <table className="table table-bordered">
          <thead>
            <tr>
              <th>
                {storedLanguage === "hi"
                  ? othersData.hi.SERIAL_NO
                  : othersData.en.SERIAL_NO}
              </th>
              <th>
                {storedLanguage === "hi"
                  ? othersData.hi.ITEM_NAME
                  : othersData.en.ITEM_NAME}
              </th>
              <th>
                {storedLanguage === "hi"
                  ? titlesData.hi.UNIT
                  : titlesData.en.UNIT}
              </th>
              {previousYearMonthPayload && previousYearMonthPayload.month && (
                <>
                  <th>
                    {(() => {
                      const monthName = iipTitleData[storedLanguage]?.MONTHS?.[
                        previousYearMonthPayload.month
                      ] || previousYearMonthPayload.month;
                      const shortMonth = typeof monthName === 'string' 
                        ? monthName.substring(0, 3).charAt(0).toUpperCase() + monthName.substring(1, 3).toLowerCase()
                        : monthName;
                      const shortYear = String(previousYearMonthPayload.year).slice(-2);
                      return `${shortMonth},${shortYear} ${storedLanguage === "hi" ? iipTitleData.hi.QUANTITY : iipTitleData.en.QUANTITY}`;
                    })()}
                  </th>
                  <th>
                    {(() => {
                      const monthName = iipTitleData[storedLanguage]?.MONTHS?.[
                        previousYearMonthPayload.month
                      ] || previousYearMonthPayload.month;
                      const shortMonth = typeof monthName === 'string' 
                        ? monthName.substring(0, 3).charAt(0).toUpperCase() + monthName.substring(1, 3).toLowerCase()
                        : monthName;
                      const shortYear = String(previousYearMonthPayload.year).slice(-2);
                      return `${shortMonth},${shortYear} ${storedLanguage === "hi" ? iipTitleData.hi.VALUE_IN_RS : iipTitleData.en.VALUE_IN_RS}`;
                    })()}
                  </th>
                  <th>
                    {(() => {
                      const monthName = iipTitleData[storedLanguage]?.MONTHS?.[
                        previousYearMonthPayload.month
                      ] || previousYearMonthPayload.month;
                      const shortMonth = typeof monthName === 'string' 
                        ? monthName.substring(0, 3).charAt(0).toUpperCase() + monthName.substring(1, 3).toLowerCase()
                        : monthName;
                      const shortYear = String(previousYearMonthPayload.year).slice(-2);
                      return `${shortMonth},${shortYear} ${storedLanguage === "hi" ? iipTitleData.hi.VARIATION : iipTitleData.en.VARIATION}`;
                    })()}
                  </th>
                </>
              )}
              {previousMonthPayload && previousMonthPayload.month && (
                <>
                  <th>
                    {(() => {
                      const monthName = iipTitleData[storedLanguage]?.MONTHS?.[
                        previousMonthPayload.month
                      ] || previousMonthPayload.month;
                      const shortMonth = typeof monthName === 'string' 
                        ? monthName.substring(0, 3).charAt(0).toUpperCase() + monthName.substring(1, 3).toLowerCase()
                        : monthName;
                      const shortYear = String(previousMonthPayload.year).slice(-2);
                      return `${shortMonth},${shortYear} ${storedLanguage === "hi" ? iipTitleData.hi.QUANTITY : iipTitleData.en.QUANTITY}`;
                    })()}
                  </th>
                  <th>
                    {(() => {
                      const monthName = iipTitleData[storedLanguage]?.MONTHS?.[
                        previousMonthPayload.month
                      ] || previousMonthPayload.month;
                      const shortMonth = typeof monthName === 'string' 
                        ? monthName.substring(0, 3).charAt(0).toUpperCase() + monthName.substring(1, 3).toLowerCase()
                        : monthName;
                      const shortYear = String(previousMonthPayload.year).slice(-2);
                      return `${shortMonth},${shortYear} ${storedLanguage === "hi" ? iipTitleData.hi.VALUE_IN_RS : iipTitleData.en.VALUE_IN_RS}`;
                    })()}
                  </th>
                  <th>
                    {(() => {
                      const monthName = iipTitleData[storedLanguage]?.MONTHS?.[
                        previousMonthPayload.month
                      ] || previousMonthPayload.month;
                      const shortMonth = typeof monthName === 'string' 
                        ? monthName.substring(0, 3).charAt(0).toUpperCase() + monthName.substring(1, 3).toLowerCase()
                        : monthName;
                      const shortYear = String(previousMonthPayload.year).slice(-2);
                      return `${shortMonth},${shortYear} ${storedLanguage === "hi" ? iipTitleData.hi.VARIATION : iipTitleData.en.VARIATION}`;
                    })()}
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {(() => {
              // Get the maximum length of both arrays
              const previousYearItems = previousYearMonthData?.[0]?.detailsOfItemsManufactured || [];
              const previousMonthItems = previousMonthData?.[0]?.detailsOfItemsManufactured || [];
              const currentMonthItems = manufacturedData || [];
              const maxLength = Math.max(previousYearItems.length, previousMonthItems.length, currentMonthItems.length);

              return Array.from({ length: maxLength }, (_, index) => {
                const previousYearItem = previousYearItems[index];
                const previousMonthItem = previousMonthItems[index];
                const currentMonthItem = currentMonthItems[index];

                // Calculate variation percentage for previousYearMonth (only Quantity)
                const previousYearVariation = previousYearItem && currentMonthItem
                  ? (() => {
                      const currentQty = parseFloat(currentMonthItem.itemQuantity) || 0;
                      const previousQty = parseFloat(previousYearItem.itemQuantity) || 0;
                      
                      // Calculate percentage variation for Quantity only
                      const qtyPercentage = previousQty !== 0 
                        ? ((currentQty - previousQty) / previousQty) * 100 
                        : (currentQty !== 0 ? 100 : 0);
                      
                      return qtyPercentage;
                    })()
                  : null;

                // Calculate variation percentage for previousMonth (only Quantity)
                const previousMonthVariation = previousMonthItem && currentMonthItem
                  ? (() => {
                      const currentQty = parseFloat(currentMonthItem.itemQuantity) || 0;
                      const previousQty = parseFloat(previousMonthItem.itemQuantity) || 0;
                      
                      // Calculate percentage variation for Quantity only
                      const qtyPercentage = previousQty !== 0 
                        ? ((currentQty - previousQty) / previousQty) * 100 
                        : (currentQty !== 0 ? 100 : 0);
                      
                      return qtyPercentage;
                    })()
                  : null;

                return (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      {previousYearItem?.itemCode === "000000"
                        ? previousYearItem?.itemName || previousMonthItem?.itemName || currentMonthItem?.itemName || ""
                        : previousYearItem
                        ? `${previousYearItem.itemCode} - ${previousYearItem.itemName}`
                        : previousMonthItem
                        ? `${previousMonthItem.itemCode} - ${previousMonthItem.itemName}`
                        : currentMonthItem?.itemName || ""}
                    </td>
                    <td>
                      {(previousYearItem?.getUnitCodesdata?.find(
                        (unit) => unit.unitCode === previousYearItem.unitCode
                      )?.unitName || previousYearItem?.unitCode) ||
                        (previousMonthItem?.getUnitCodesdata?.find(
                          (unit) => unit.unitCode === previousMonthItem.unitCode
                        )?.unitName || previousMonthItem?.unitCode) ||
                        (currentMonthItem?.getUnitCodesdata?.find(
                          (unit) => unit.unitCode === currentMonthItem.unitCode
                        )?.unitName || currentMonthItem?.unitCode) ||
                        ""}
                    </td>
                    {previousYearMonthPayload && previousYearMonthPayload.month && (
                      <>
                        <td>{previousYearItem?.itemQuantity || ""}</td>
                        <td>
                          {previousYearItem?.itemPrice !== null && previousYearItem?.itemPrice !== undefined
                            ? formatIipValueByYear(
                                previousYearItem.itemPrice,
                                previousYearMonthPayload?.year
                              )
                            : ""}
                        </td>
                        <td>
                          {previousYearVariation !== null
                            ? renderQtyVariationPercent(previousYearVariation)
                            : null}
                        </td>
                      </>
                    )}
                    {previousMonthPayload && previousMonthPayload.month && (
                      <>
                        <td>{previousMonthItem?.itemQuantity || ""}</td>
                        <td>
                          {previousMonthItem?.itemPrice !== null && previousMonthItem?.itemPrice !== undefined
                            ? formatIipValueByYear(
                                previousMonthItem.itemPrice,
                                previousMonthPayload?.year
                              )
                            : ""}
                        </td>
                        <td>
                          {previousMonthVariation !== null
                            ? renderQtyVariationPercent(previousMonthVariation)
                            : null}
                        </td>
                      </>
                    )}
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>

        <div className="row">
          <div className="col-12 text-center pt-3">
            {
              <button
                type="button"
                className="btn btn_orange mx-1"
                onClick={repeatPreviousMonthData}
                disabled={
                  status === "0" || !hasRepeatablePreviousMonthData(previousMonthData)
                }
              >
                Repeat Previous Month
              </button>
            }
            {
              <button
                type="button"
                className="btn btn_orange mx-1"
                onClick={addRow}
                disabled={status === "0" || isAddRow}
              >
                Add Row
              </button>
            }
            {
              <button
                onClick={deleteRows}
                type="button"
                className="btn btn_orange mx-1"
                disabled={isDeleteRow}
              >
                Delete Row
              </button>
            }
            {
              <button
                onClick={(e) => handleManufacturingSectorData(e, "SAVE")}
                type="button"
                className={`btn btn_orange mx-1`}
                disabled={isSaveBtnDisable}
              >
                Save
              </button>
            }

            {(userInfoObject.userLevel === "DISTRICT LEVEL" ||
              userInfoObject.userLevel === "DIVISION LEVEL") && (
                <button
                  onClick={(e) => handleManufacturingSectorData(e, "SUBMIT")}
                  type="submit"
                  className={`btn btn_orange mx-1 ${isSubmitBtnDisable ? "disabled" : ""
                    }`}
                >
                  Submit
                </button>
              )}

            {userInfoObject.userLevel === "HEAD QUARTER" && (
              <>
                <button
                  onClick={(e) => handleManufacturingSectorData(e, "ACCEPT")}
                  type="button"
                  className={`btn btn_orange mx-1`}
                  disabled={isAcceptBtnDisable}
                >
                  Accept
                </button>

                <button
                  onClick={(e) => handleManufacturingSectorData(e, "REJECT")}
                  type="button"
                  className={`btn btn_orange mx-1`}
                  disabled={isRejectBtnDisable}
                >
                  Reject
                </button>
              </>
            )}

            <button
              type="reset"
              className="btn btn_orange mx-1"
              onClick={resetForm}
            >
              Reset
            </button>

            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={(e) => navigate("/dashboard")}
            >
              Cancel
            </button>

            <button
              onClick={(e) =>
                handleManufacturingSectorData(e, "SAVE FOR NEXT MONTH")
              }
              type="button"
              className={`btn btn_orange mx-1`}
            >
              Save for next month
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default ManufacturingSectorData;
